import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";
import { FONT_FAMILY_SANS_STRING } from "../constants/fonts";

const STRIPE_JS_URL = "https://js.stripe.com/v3/";

// Nexus billing_period → Apple Pay recurring interval. A period missing here (new Nexus cadence)
// falls back to the default Amount Pending sheet rather than guessing.
const RECURRING_INTERVALS = {
  monthly: { unit: "month", count: 1 },
  monthly_anchored: { unit: "month", count: 1 },
  quarterly: { unit: "month", count: 3 },
  annual: { unit: "year", count: 1 },
  rolling_30: { unit: "day", count: 30 },
};

// The checkout form and the standalone payment-method page put Stripe.js in the head, but the
// customer portal renders the same form under a layout that has no hook for it. Load it on demand
// so the surface works wherever it's rendered; the shared promise keeps concurrent mounts to one tag.
let stripeJsLoad = null;
function loadStripeJs() {
  if (window.Stripe) return Promise.resolve();

  // Always append our own tag rather than adopting one already in the DOM: a tag that has already
  // errored will never fire load or error again, so listening to it would hang forever with no
  // catch branch to show the customer. A duplicate src is cheap and Stripe.js is idempotent.
  stripeJsLoad ||= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = STRIPE_JS_URL;
    script.addEventListener("load", () => {
      if (window.Stripe) resolve();
      else reject(new Error("Stripe.js loaded without defining Stripe"));
    });
    script.addEventListener("error", () => {
      stripeJsLoad = null;
      script.remove();
      reject(new Error("Stripe.js failed to load"));
    });
    document.head.appendChild(script);
  });

  return stripeJsLoad;
}

// Stripe Elements surface for Nexus checkout forms and portal card updates. A Payment Element in
// deferred setup mode mounts with the selected location's processor credentials, so Apple Pay and
// Google Pay render alongside card entry when eligible. Submission is two interrupts: a prepended
// elements.submit() (opens the wallet sheet inside the user gesture, before slower interrupts like
// recaptcha), then a normally-ordered stage-and-confirm that runs after the form's other gates, so
// recaptcha and validation still precede card capture. Nexus issues card-only setup intents, so the
// element must stay card-only.
export default class extends Controller {
  static outlets = ["payment-update"];
  static values = {
    setupIntentUrl: String,
    processorsByLocation: Object,
    managementUrl: String,
  };
  static targets = [
    "customerId",
    "vehicleId",
    "setupIntentId",
    "cardProcessorId",
    "cardElement",
    "stripeError",
    "selectLocationHint",
  ];

  connect() {
    this.paymentUpdateOutlet.addAsyncSubmitInterrupt(this.submitElements.bind(this), { prepend: true });
    this.paymentUpdateOutlet.addAsyncSubmitInterrupt(this.confirmPayment.bind(this));

    this.boundWalletDisplay = this.walletDisplayChanged.bind(this);
    window.addEventListener("nexusWalletDisplay", this.boundWalletDisplay);

    this.locationSelect = document.querySelector('[name="nexus_location_id"]');
    if (this.locationSelect) {
      this.boundMount = this.mountCardForLocation.bind(this);
      this.locationSelect.addEventListener("change", this.boundMount);
    }
    this.mountCardForLocation();
  }

  disconnect() {
    // Supersede any mount still awaiting Stripe.js so it doesn't touch a torn-down element.
    this.mountGeneration = (this.mountGeneration || 0) + 1;
    window.removeEventListener("nexusWalletDisplay", this.boundWalletDisplay);
    if (this.locationSelect && this.boundMount) {
      this.locationSelect.removeEventListener("change", this.boundMount);
    }
    this.unmountCard();
  }

  walletDisplayChanged(event) {
    this.walletDisplay = event.detail;
    this.applyWalletDisplay();
  }

  applyWalletDisplay() {
    // undefined = no checkout form ever dispatched (the payment-method update page) — leave the
    // element's default save-card sheet alone.
    if (this.walletDisplay === undefined || !this.paymentElement) return;

    // The dispatch fires on every estimate-feeding keystroke; skip the cross-iframe element
    // update when the payload hasn't actually changed.
    const applePay = this.applePayOptions() ?? {};
    const serialized = JSON.stringify(applePay);
    if (serialized === this.appliedApplePay) return;

    this.appliedApplePay = serialized;
    this.paymentElement.update({ applePay });
  }

  // Everything shown on the sheet is Nexus-sourced (quote total, sellable name, billing cadence);
  // the CRM contributes only the portal management URL Apple requires. Any gap falls back to the
  // default Amount Pending sheet — a missing number beats a wrong one. Only recurring plans get a
  // custom sheet: Apple's deferred format requires a genuinely future charge date, and Nexus
  // charges one-time purchases immediately after checkout.
  applePayOptions() {
    const display = this.walletDisplay;
    const managementURL = this.managementUrlValue;
    if (display?.kind !== "recurring" || !managementURL) return null;

    const interval = RECURRING_INTERVALS[display.billingPeriod];
    if (!interval) return null;

    const recurringPaymentRequest = {
      paymentDescription: display.label,
      managementURL,
      regularBilling: {
        amount: display.amountCents,
        label: display.label,
        recurringPaymentIntervalUnit: interval.unit,
        recurringPaymentIntervalCount: interval.count,
      },
    };
    // A discounted first charge rides as Apple's trial line, so the sheet reads "$X for the first
    // <period>, then $Y per <period>" instead of showing the discount as the recurring price.
    // Zero is a real amount here — a 100%-off first charge — hence the null check.
    // Start/end dates stay unset on both lines (Apple treats them as optional): the quote carries
    // no renewal date, and a guessed one would be wrong for monthly_anchored plans, whose next
    // charge rides the anchor day rather than one interval from today.
    if (display.trialAmountCents != null) {
      recurringPaymentRequest.trialBilling = {
        amount: display.trialAmountCents,
        label: display.label,
        recurringPaymentIntervalUnit: interval.unit,
        recurringPaymentIntervalCount: interval.count,
      };
    }
    return { recurringPaymentRequest };
  }

  selectedLocationId() {
    if (this.locationSelect?.value) return this.locationSelect.value;

    const locationIds = Object.keys(this.processorsByLocationValue);
    return locationIds.length === 1 ? locationIds[0] : null;
  }

  async mountCardForLocation() {
    // Every call claims a generation. Anything that awaits then finds itself superseded bails,
    // which mountedKey alone can't detect: a newer call that unmounted (location cleared, or one
    // with no processor) leaves mountedKey null, and the older call would happily mount its stale
    // location on top of it.
    const generation = (this.mountGeneration = (this.mountGeneration || 0) + 1);
    const locationId = this.selectedLocationId();
    const processor = locationId && this.processorsByLocationValue[locationId];

    if (!processor) {
      this.unmountCard();
      this.selectLocationHintTarget.hidden = false;
      this.cardElementTarget.hidden = true;
      return;
    }

    const mountKey = `${processor.publishable_key}/${processor.stripe_account_id || ""}`;
    if (this.mountedKey === mountKey) return;

    try {
      await loadStripeJs();
    } catch {
      if (generation !== this.mountGeneration) return;
      this.setErrorMessage("We could not load the secure card form. Please refresh and try again.");
      return;
    }
    if (generation !== this.mountGeneration) return;

    this.selectLocationHintTarget.hidden = true;
    this.cardElementTarget.hidden = false;

    this.unmountCard();
    this.stripe = Stripe(
      processor.publishable_key,
      processor.stripe_account_id ? { stripeAccount: processor.stripe_account_id } : {}
    );
    this.mountedKey = mountKey;

    this.elements = this.stripe.elements({
      mode: "setup",
      currency: "usd",
      paymentMethodTypes: ["card"],
      appearance: this.appearance(),
    });
    this.paymentElement = this.elements.create("payment");
    this.paymentElement.mount(this.cardElementTarget);
    this.paymentElement.on("change", () => this.paymentUpdateOutlet.enableSubmit());
    this.applyWalletDisplay();
  }

  unmountCard() {
    this.paymentElement?.destroy();
    this.paymentElement = null;
    this.elements = null;
    this.stripe = null;
    this.mountedKey = null;
    this.appliedApplePay = null;
  }

  appearance() {
    return {
      variables: {
        fontFamily: FONT_FAMILY_SANS_STRING,
        fontSizeBase: "16px",
        colorDanger: "#fa755a",
      },
    };
  }

  async submitElements() {
    if (!this.paymentElement) {
      this.setErrorMessage("Please select a location before entering your payment details.");
      return false;
    }

    try {
      const { error } = await this.elements.submit();
      if (error) {
        this.setErrorMessage(error.message);
        return false;
      }
      this.submission = {
        stripe: this.stripe,
        elements: this.elements,
        locationId: this.selectedLocationId(),
      };
      this.clearErrorMessage();
      return true;
    } catch (error) {
      this.setErrorMessage(error?.message);
      return false;
    }
  }

  async confirmPayment() {
    // The form's other interrupts (recaptcha, validation) run between submitElements and here — an
    // interactive gap in which a location change can remount the element against a different
    // processor, or (same processor, no remount) silently move the order to a location the staged
    // card wouldn't match. Confirm must use the instances the payment details were collected on,
    // staging must target their location, and that location must still be the selected one.
    const submission = this.submission;
    if (
      !submission ||
      submission.elements !== this.elements ||
      submission.locationId !== this.selectedLocationId()
    ) {
      this.setErrorMessage("The location changed while we were processing. Please submit again.");
      return false;
    }

    try {
      const staged = await this.stageSetupIntent(submission.locationId);
      if (!staged) return false;

      const { error } = await submission.stripe.confirmSetup({
        elements: submission.elements,
        clientSecret: staged.client_secret,
        redirect: "if_required",
      });
      if (error) {
        this.setErrorMessage(error.message);
        return false;
      }

      // Checkout only: the payment method update form resolves the customer server-side
      // from the membership, so it renders no customer or vehicle id fields.
      if (this.hasCustomerIdTarget) this.customerIdTarget.value = staged.nexus_customer_id;
      if (this.hasVehicleIdTarget) this.vehicleIdTarget.value = staged.nexus_vehicle_id || "";
      this.setupIntentIdTarget.value = staged.setup_intent_id;
      this.cardProcessorIdTarget.value = staged.card_processor_id;
      this.clearErrorMessage();
      return true;
    } catch (error) {
      this.setErrorMessage(error?.message);
      return false;
    }
  }

  async stageSetupIntent(locationId) {
    const response = await post(this.setupIntentUrlValue, {
      body: JSON.stringify(this.withoutBlanks({
        nexus_location_id: locationId,
        customer_first_name: this.formValue("customer[first_name]"),
        customer_last_name: this.formValue("customer[last_name]"),
        customer_email: this.formValue("customer[email]"),
        customer_phone: this.formValue("customer[phone]"),
        membership_license_plate: this.formValue("membership[license_plate]"),
        membership_license_plate_state: this.formValue("membership[license_plate_state]"),
        loyalty_program_redemption_id: this.formValue("loyalty_program_redemption_id"),
        // Update-form only: identifies the membership whose card is being replaced. Absent on
        // checkout forms, where the endpoint resolves everything from the form and typed contact.
        ticket_id: this.formValue("ticket_id"),
        short_link_code: this.formValue("short_link_code"),
        session_id: this.formValue("session_id"),
        embedded_in_customer_portal: this.formValue("embedded_in_customer_portal"),
      })),
      contentType: "application/json",
      responseKind: "json",
    });

    if (!response.ok) {
      const body = await response.json.catch(() => ({}));
      this.setErrorMessage(body?.error);
      return null;
    }

    return response.json;
  }

  // formValue returns "" for fields the current form doesn't render. Sending those keys makes an
  // absent identifier look present to the server, so drop them rather than posting empty strings.
  withoutBlanks(body) {
    return Object.fromEntries(Object.entries(body).filter(([, value]) => value !== ""));
  }

  formValue(name) {
    return this.element.closest("form")?.querySelector(`[name="${name}"]`)?.value || "";
  }

  setErrorMessage(message) {
    this.stripeErrorTarget.textContent = message || "Something went wrong. Please try again.";
    this.stripeErrorTarget.hidden = false;
  }

  clearErrorMessage() {
    this.stripeErrorTarget.textContent = "";
    this.stripeErrorTarget.hidden = true;
  }
}
